/**
 * Repositorio de sesiones.
 *
 * La interfaz NUNCA toca Dexie directamente: pasa por aquí. El test de
 * fronteras lo verifica.
 */

import { liveQuery, type Observable } from 'dexie';

import { nuevoId } from '../domain/modelo/ids.js';
import type {
  EstadoSesion,
  PasoRegistrado,
  SesionRegistrada,
  TipoSesion,
} from '../domain/modelo/sesion.js';
import type { DuracionSesion } from '../domain/modelo/ajustes.js';
import type { SemanaCheckpoint } from '../domain/modelo/sesion.js';
import {
  metadatosBorrados,
  metadatosEditados,
  metadatosNuevos,
} from '../domain/modelo/sincronizacion.js';
import { db, escribir } from './db.js';

export interface DatosSesionNueva {
  tipo: TipoSesion;
  sessionKey: string;
  semana: number;
  duracionPlanificada: DuracionSesion;
  curriculumVersion: string;
  checkpointSemana?: SemanaCheckpoint;
  /** Solo en mediciones: qué muestra de listening le toca a esta. */
  fuenteListeningClave?: string;
  pasos: PasoRegistrado[];
}

function visible(sesion: SesionRegistrada): boolean {
  return !sesion.deleted;
}

export const sesionesRepo = {
  /** Todas las sesiones vivas. El soft delete se filtra en memoria. */
  async listar(): Promise<SesionRegistrada[]> {
    const todas = await db.sesiones.toArray();
    return todas.filter(visible);
  },

  /**
   * Observable reactivo para la interfaz.
   *
   * `liveQuery` es lo que hace innecesaria una librería de estado global: toda
   * escritura en la base repinta sola las pantallas que dependen de ella.
   */
  observarTodas(): Observable<SesionRegistrada[]> {
    return liveQuery(async () => sesionesRepo.listar());
  },

  async obtener(id: string): Promise<SesionRegistrada | undefined> {
    const sesion = await db.sesiones.get(id);
    return sesion && visible(sesion) ? sesion : undefined;
  },

  async crear(datos: DatosSesionNueva): Promise<SesionRegistrada> {
    const sesion: SesionRegistrada = {
      id: nuevoId(),
      ...datos,
      segundosReales: 0,
      iniciadaEn: new Date().toISOString(),
      estado: 'en_progreso',
      pasoActual: 0,
      autoevaluaciones: {},
      grabacionIds: [],
      ...metadatosNuevos(),
    };
    await escribir('crear la sesión', () => db.sesiones.add(sesion));
    return sesion;
  },

  /**
   * Guarda la sesión entera de una sola escritura.
   *
   * Es lo que hace posible reanudar sin estado parcial: los pasos van dentro de
   * la fila, así que no hay forma de que la sesión y sus pasos queden en
   * desacuerdo si la escritura se interrumpe.
   */
  async guardar(sesion: SesionRegistrada): Promise<SesionRegistrada> {
    const actualizada: SesionRegistrada = {
      ...sesion,
      ...metadatosEditados(sesion),
    };
    await escribir('guardar la sesión', () => db.sesiones.put(actualizada));
    return actualizada;
  },

  async cambiarEstado(id: string, estado: EstadoSesion): Promise<void> {
    const sesion = await sesionesRepo.obtener(id);
    if (!sesion) return;
    const completadaEn =
      estado === 'completada' || estado === 'parcial'
        ? new Date().toISOString()
        : sesion.completadaEn;
    await sesionesRepo.guardar({ ...sesion, estado, completadaEn });
  },

  /** Soft delete: un borrado duro no se propagaría a los otros dispositivos. */
  async borrar(id: string): Promise<void> {
    const sesion = await db.sesiones.get(id);
    if (!sesion) return;
    await escribir('borrar la sesión', () =>
      db.sesiones.put({ ...sesion, ...metadatosBorrados(sesion) }),
    );
  },

  /** Filas con cambios pendientes de empujar. La cola de la Fase 8. */
  async pendientesDeSincronizar(): Promise<SesionRegistrada[]> {
    const todas = await db.sesiones.toArray();
    return todas.filter((s) => s.dirty);
  },
};
